<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import OneVideo from "./OneVideo.vue";
import {
  speakerInSharedState,
  speakerSharePosition,
} from "../states/speakerInSharedState";
import { getUserById } from "../states/usersState";
import { viewState } from "../states/viewState";

const props = defineProps<{
  hasMove: boolean;
}>();

const speakers = reactive<
  { userId: string; avatar: string; displayedName: string }[]
>([]);
const size = {
  width: 356,
  height: 200.25,
};
watch(
  () => speakerInSharedState.value,
  (next, prev) => {
    if (next && next !== prev) {
      const user = getUserById(next);
      if (user)
        speakers.unshift({
          userId: user.id,
          avatar: user.avatar || "",
          displayedName: user.displayedName,
        });
      setTimeout(() => {
        if (speakers.length > 1) {
          speakers.pop();
        }
      }, 1300);
    }
  },
  { immediate: true },
);
const lastCursorPos = {
  x: 0,
  y: 0,
};
const padding = 8;
const startRelativePos = {
  x: 0,
  y: 0,
};

const resizeConstraints = reactive({
  y: 0,
  x: 0,
});

const isBottom = ref(false);
const isLeft = ref(false);

const onresize = () => {
  const vArea = document.querySelector(
    ".active-room-container .speaker-container .main-content",
  )!;
  if (!vArea) {
    setTimeout(onresize, 100);
    return;
  }
  const w = vArea.clientWidth;
  const h = vArea.clientHeight;
  const maxX = w - size.width - padding;
  const maxY = h - size.height - padding;
  resizeConstraints.x = maxX;
  resizeConstraints.y = maxY;
  if (isBottom.value) {
    speakerSharePosition.y = maxY;
  }
  if (isLeft.value) {
    speakerSharePosition.x = maxX;
  }
};
const finalPosition = computed(() => {
  return {
    y: Math.min(speakerSharePosition.y, resizeConstraints.y),
    x: Math.min(speakerSharePosition.x, resizeConstraints.x),
  };
});
let observer: ResizeObserver | null = null;
const observeEl = () => {
  const vArea = document.querySelector(
    ".active-room-container .speaker-container .main-content",
  )!;
  if (!vArea) {
    setTimeout(observeEl, 100);
    return;
  }
  observer?.observe(vArea);
};
onMounted(() => {
  onresize();
  observer = new ResizeObserver(onresize);
  observeEl();
});
onUnmounted(() => {
  observer?.disconnect();
});

const mouseMove = (e: MouseEvent) => {
  let xLock = false;
  let yLock = false;
  if (e.clientX <= 0 || e.clientX >= window.innerWidth) {
    xLock = true;
  }
  if (e.clientY <= 0 || e.clientY >= window.innerHeight) {
    yLock = true;
  }
  const dx = e.clientX - lastCursorPos.x;
  const dy = e.clientY - lastCursorPos.y;
  const vArea = document.querySelector(
    ".active-room-container .speaker-container .main-content",
  )!;
  const w = vArea.clientWidth;
  const h = vArea.clientHeight;
  lastCursorPos.x = e.clientX;
  lastCursorPos.y = e.clientY;
  const newRight = xLock ? speakerSharePosition.x : speakerSharePosition.x - dx;
  const newTop = yLock ? speakerSharePosition.y : speakerSharePosition.y + dy;
  const maxRight = w - size.width - padding;
  const maxTop = h - size.height - padding;
  if (newRight < 8) {
    speakerSharePosition.x = 8;
  } else if (newRight > maxRight) {
    speakerSharePosition.x = maxRight;
  } else {
    speakerSharePosition.x = newRight;
  }
  if (newTop < 8) {
    speakerSharePosition.y = 8;
  } else if (newTop > maxTop) {
    speakerSharePosition.y = maxTop;
  } else {
    speakerSharePosition.y = newTop;
  }
  if(newTop >= maxTop) {
    isBottom.value = true;
  } else {
    isBottom.value = false;
  }
  if(newRight >= maxRight) {
    isLeft.value = true;
  } else {
    isLeft.value = false;
  }
};
const mouseDown = (e: MouseEvent) => {
  if (!props.hasMove) return;
  window.addEventListener("mousemove", mouseMove);
  window.addEventListener("mouseup", mouseUp);
  lastCursorPos.x = e.clientX;
  lastCursorPos.y = e.clientY;
  const target = e.target as HTMLDivElement;
  const pos = target.getBoundingClientRect();
  startRelativePos.x = e.clientX - pos.left;
  startRelativePos.y = e.clientY - pos.top;
};
const mouseUp = () => {
  window.removeEventListener("mousemove", mouseMove);
};
const pipMode = computed({
  get() {
    return viewState.pipMode;
  },
  set(val: boolean) {
    viewState.pipMode = val;
  },
});
const speakerEl = ref<HTMLElement | null>(null);
const animatedHasMove = ref(props.hasMove);
const savedResizeConstraints = reactive({
  y: 0,
  x: 0,
});
const savedPosition = reactive({
  y: 0,
  x: 0,
});
const animationProcess = ref(false);
function setTransition() {
  animationProcess.value = true;
  if (speakerEl.value) {
    speakerEl.value.style.transition = "transform 0.3s ease-in-out";
    setTimeout(() => {
      if (speakerEl.value) {
        speakerEl.value.style.transition = "none";
      }
      animationProcess.value = false;
    }, 400);
  } else {
    animationProcess.value = false;
  }
}
watch(
  () => props.hasMove,
  (next, prev) => {
    if (next && !prev) {
      savedResizeConstraints.y = resizeConstraints.y;
      savedResizeConstraints.x = resizeConstraints.x + 367;
      setTransition();
      animatedHasMove.value = true;
    } else if (!next && prev) {
      savedPosition.y = speakerSharePosition.y;
      savedPosition.x = speakerSharePosition.x;
      savedResizeConstraints.y = resizeConstraints.y;
      savedResizeConstraints.x = resizeConstraints.x + 367;
      setTransition();
      animatedHasMove.value = false;
    }
  },
);
const transform = computed(() => {
  if (animatedHasMove.value) {
    if (animationProcess.value) {
      const top = Math.min(savedPosition.y, savedResizeConstraints.y);
      const right = Math.min(savedPosition.x, savedResizeConstraints.x);
      return `translate(${-right + 8}px, ${top - 8}px)`;
    } else {
      return `translate(${-finalPosition.value.x + 8}px, ${finalPosition.value.y - 8}px)`;
    }
  } else return "none";
});
</script>
<template>
  <div
    class="screen-speaker-item"
    ref="speakerEl"
    :style="{
      position: 'absolute',
      zIndex: '100',
      cursor: hasMove ? 'move' : 'default',
      top: '8px',
      right: '8px',
      transform,
      display: pipMode || viewState.fullScreenChat ? 'none' : 'flex',
    }"
    @mousedown="mouseDown"
  >
    <div
      v-if="!pipMode"
      class="close"
      draggable="false"
      @dragstart.prevent
      @click="viewState.showSpeakerInDemo = false"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.00897 5.30371L10.0176 10.3124M10.0176 10.3124L15.0263 15.3211M10.0176 10.3124L5.00897 15.3211M10.0176 10.3124L15.0263 5.30371"
          stroke="#ECECEC"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <TransitionGroup v-if="!pipMode" name="fade-screen-speaker">
      <OneVideo
        v-for="(s, idx) in speakers"
        :user-id="s.userId"
        :avatar="s.avatar"
        :displayed-name="s.displayedName"
        :size="size"
        :key="s.userId"
        class="one-item"
        draggable="false"
        @dragstart.prevent
        :style="{
          'z-index': idx,
        }"
      />
    </TransitionGroup>
  </div>
</template>
<style lang="scss" scoped>
.one-item {
  position: absolute;
}

.pip {
  width: 36px;
  height: 36px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background-color: rgba(73, 73, 73, 0.85);
  z-index: 10;
  display: none;
  cursor: pointer;
  flex-shrink: 0;
}

.close {
  width: 36px;
  height: 36px;
  position: absolute;
  top: 8px;
  right: 8px;
  border-radius: 50%;
  background-color: rgba(73, 73, 73, 0.85);
  z-index: 10;
  display: none;
  cursor: pointer;
  flex-shrink: 0;
}

.close:hover {
  background-color: #494949;
}

.close svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.screen-speaker-item {
  position: relative;
  width: 356px;
  height: 200.25px;
  border-radius: 8px;
  overflow: hidden;

  &:hover {
    .close {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pip {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
<style>
.fade-screen-speaker-leave-active {
  transition: opacity 120ms ease-in;
}

.fade-screen-speaker-leave-to {
  opacity: 0;
}
</style>
